// src/features/bookings/components/BookingForm/index.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../../../api/axiosInstance';
import styles from '../../../../components/Form.module.css';
import { useAuth } from '../../../auth/context/AuthContext';
import ClientModal from '../../../clients/components/ClientModal';
import ClientSelect from '../../../clients/components/ClientSelect';
import { BOOKING_STATUS_LABELS, BOOKING_TYPE_LABELS } from '../../../../shared/utils/formatters';

import BasicInfoCard          from './cards/BasicInfoCard';
import GeneralInfoCard        from './cards/GeneralInfoCard';
import LegacyGeneralInfoCard  from './cards/LegacyGeneralInfoCard';
import ConditionalDetailsCard from './cards/ConditionalDetailsCard';

const extractBookingsList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const formatBookingConflict = (booking) => {
  const clientName = booking.client?.name || 'عميل غير محدد';
  const typeLabel = BOOKING_TYPE_LABELS[booking.booking_type] || booking.booking_type || 'حجز';
  const statusLabel = BOOKING_STATUS_LABELS[booking.status] || booking.status || '';
  const timeLabel = booking.shooting_time ? ` - ${String(booking.shooting_time).slice(0, 5)}` : '';
  return `- ${clientName} | ${typeLabel}${timeLabel}${statusLabel ? ` | ${statusLabel}` : ''}`;
};

function BookingForm({ onBookingCreated }) {
  const [clients,       setClients]       = useState([]);
  const [loadingClients,setLoadingClients] = useState(true);
  const { hasPermission, loggedInUser }   = useAuth();
  const [isClientModalOpen, setClientModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);

  const [bookingData, setBookingData] = useState({
    client_id: '', booking_type: '', booking_date: '',
    total_price: '', shooting_location: '', shooting_time: '', notes: '',
  });

  const initialGraduationOffer = {
    name: 'العرض الأساسي', num_students: '', offer_price: '', printing_cost: '3680',
  };

  const [graduationDetails, setGraduationDetails] = useState({
    college: '', department: '', representative_name: '', representative_phone: '',
    extra_photos_count: '', other_revenues: '', additional_revenues: '',
    extra_photo_price: '1500',
    offers: [initialGraduationOffer],
  });

  const [weddingDetails, setWeddingDetails] = useState({
    groom_name: '', bride_name: '', package_details: '', event_plan: '',
  });
  const [kidsDetails, setKidsDetails] = useState({
    child_name: '', child_age: '', child_gender: 'boy', session_theme: '',
  });
  const [eventDetails, setEventDetails] = useState({
    event_name: '', expected_attendees: '', event_schedule: '',
  });

  const allowedBookingTypes = useMemo(() => {
    const allTypes = [
      { value: 'graduation',        label: 'حفلات التخرج'    },
      { value: 'wedding',           label: 'الأعراس'          },
      { value: 'kids',              label: 'تصوير أطفال'      },
      { value: 'event',             label: 'مناسبات'           },
      { value: 'special',           label: 'طلب خاص'          },
      { value: 'digital_marketing', label: 'تسويق إلكتروني'   },
    ];
    if (loggedInUser?.is_admin || loggedInUser?.is_superuser) return allTypes;

    if (!loggedInUser?.departments?.length) return [];
    const allowed = loggedInUser.departments.map(d => d.associated_booking_type);
    return allTypes.filter(type => allowed.includes(type.value));
  }, [loggedInUser]);

  const fetchClients = useCallback(async () => {
    if (!hasPermission('view_clients')) { setLoadingClients(false); return; }
    try {
      setLoadingClients(true);
      const response = await api.get('/clients/');
      setClients(response.data.filter(Boolean));
    } catch {
      setError('فشل في تحميل قائمة العملاء.');
    } finally {
      setLoadingClients(false);
    }
  }, [hasPermission]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleBookingChange  = (e) => setBookingData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleClientChange   = (clientId) => setBookingData(prev => ({ ...prev, client_id: clientId }));

  const handleClientSaved = useCallback((newClient) => {
    if (!newClient?.id) { setClientModalOpen(false); return; }
    setClients(prev => prev.some(c => c.id === newClient.id) ? prev : [...prev, newClient]);
    setBookingData(prev => ({ ...prev, client_id: newClient.id }));
    setClientModalOpen(false);
    setTimeout(() => fetchClients(), 1000);
  }, [fetchClients]);

  const handleGraduationDetailsChange = (e) =>
    setGraduationDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleOfferChange = (index, event) => {
    const { name, value } = event.target;
    setGraduationDetails(prev => {
      const newOffers = [...prev.offers];
      newOffers[index] = { ...newOffers[index], [name]: value };
      return { ...prev, offers: newOffers };
    });
  };

  const addOffer = () => setGraduationDetails(prev => ({
    ...prev,
    offers: [...prev.offers, {
      name: `العرض ${prev.offers.length + 1}`,
      num_students: '', offer_price: '', printing_cost: '3680',
    }],
  }));

  const removeOffer = (index) => {
    if (graduationDetails.offers.length <= 1) return;
    setGraduationDetails(prev => ({
      ...prev,
      offers: prev.offers.filter((_, i) => i !== index),
    }));
  };

  const detailChangeHandlers = {
    graduation: handleGraduationDetailsChange,
    wedding:    (e) => setWeddingDetails(prev => ({ ...prev, [e.target.name]: e.target.value })),
    kids:       (e) => setKidsDetails(prev => ({ ...prev, [e.target.name]: e.target.value })),
    event:      (e) => setEventDetails(prev => ({ ...prev, [e.target.name]: e.target.value })),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!bookingData.client_id || !bookingData.booking_type) {
      setError('يرجى اختيار العميل ونوع الحجز كحد أدنى.');
      setSubmitting(false);
      return;
    }

    if (bookingData.booking_date) {
      try {
        const conflictRes = await api.get('/bookings/', {
          params: {
            start_date: bookingData.booking_date,
            end_date: bookingData.booking_date,
            status: 'all',
            page_size: 100,
          },
        });
        const sameDayBookings = extractBookingsList(conflictRes.data);

        if (sameDayBookings.length > 0) {
          const shown = sameDayBookings.slice(0, 8).map(formatBookingConflict).join('\n');
          const more = sameDayBookings.length > 8 ? `\n... +${sameDayBookings.length - 8}` : '';
          const shouldContinue = window.confirm(
            `تنبيه الحجز المزدوج\n\nيوجد ${sameDayBookings.length} حجز/حجوزات في نفس اليوم:\n\n${shown}${more}\n\nهل تريد متابعة حفظ الحجز؟`
          );

          if (!shouldContinue) {
            setSubmitting(false);
            return;
          }
        }
      } catch {
        // إذا تعذر فحص التعارض لا نمنع الحفظ.
      }
    }

    const finalPayload = {
      ...bookingData,
      client_id: parseInt(bookingData.client_id),
    };

    if (bookingData.booking_type === 'digital_marketing') {
      delete finalPayload.shooting_time;
    }

    if (bookingData.booking_type === 'graduation') {
      delete finalPayload.total_price;

      const cleanOffers = graduationDetails.offers.map(offer => ({
        ...offer,
        num_students:  parseInt(offer.num_students)  || 0,
        offer_price:   parseFloat(offer.offer_price) || 0.0,
        printing_cost: parseFloat(offer.printing_cost) || 0.0,
      }));

      const cleanGraduationDetails = {};
      for (const [key, value] of Object.entries(graduationDetails)) {
        if (key === 'offers') cleanGraduationDetails[key] = cleanOffers;
        else if (value !== '' && value !== null) cleanGraduationDetails[key] = value;
      }
      finalPayload.graduation_details = cleanGraduationDetails;

    } else {
      finalPayload.total_price = parseFloat(finalPayload.total_price) || 0;
      const isEmpty = (obj) => Object.values(obj).every(v => v === '' || v === null || v === undefined);

      if (bookingData.booking_type === 'wedding' && !isEmpty(weddingDetails))
        finalPayload.wedding_details = weddingDetails;
      else if (bookingData.booking_type === 'kids' && !isEmpty(kidsDetails))
        finalPayload.kids_details = kidsDetails;
      else if (bookingData.booking_type === 'event' && !isEmpty(eventDetails))
        finalPayload.event_details = eventDetails;
    }

    try {
      await api.post('/bookings/', finalPayload);
      onBookingCreated();
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'حدث خطأ أثناء الحفظ. ';
      if (errorData && typeof errorData === 'object') {
        errorMessage += Object.entries(errorData)
          .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(' ') : messages}`
          ).join(' | ');
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit} className={styles.form}>

        <BasicInfoCard
          bookingData={bookingData}
          handleBookingChange={handleBookingChange}
          clients={clients}
          handleClientChange={handleClientChange}
          openClientModal={() => setClientModalOpen(true)}
          loadingClients={loadingClients}
          allowedBookingTypes={allowedBookingTypes}
        />

        {bookingData.booking_type === 'digital_marketing' ? (
          <GeneralInfoCard
            bookingData={bookingData}
            handleBookingChange={handleBookingChange}
          />
        ) : (
          bookingData.booking_type && (
            <LegacyGeneralInfoCard
              bookingData={bookingData}
              handleBookingChange={handleBookingChange}
            />
          )
        )}

        <ConditionalDetailsCard
          bookingType={bookingData.booking_type}
          details={{
            graduation: graduationDetails,
            wedding:    weddingDetails,
            kids:       kidsDetails,
            event:      eventDetails,
          }}
          handlers={detailChangeHandlers}
          offerHandlers={{ handleOfferChange, addOffer, removeOffer }}
        />

        {error && <p className={styles.errorMessage}>{error}</p>}

        <button
          type="submit"
          disabled={submitting || !hasPermission('add_booking')}
          className={styles.submitButton}
        >
          {submitting ? 'جاري الحفظ...' : 'حفظ الحجز'}
        </button>

      </form>

      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onSave={handleClientSaved}
        clientToEdit={null}
      />
    </div>
  );
}

export default BookingForm;
